/**
 * CmPhoto (Workspace v2 Slice 3c) — a round care-manager avatar that shows the
 * uploaded photo, falling back to the initials monogram on a 404/none or any
 * load error. The single shared photo+fallback component used by the People-tab
 * editor, the assignment picker, and the drawer's assigned-CM area — so a
 * missing photo is never a broken image and the fallback is identical everywhere.
 *
 * The photo is served at the view_workspace floor (every photo-rendering surface
 * holds it). `hasPhoto={false}` skips the <img> entirely (no 404 round-trip)
 * when the caller already knows there is none; `version` (e.g. Date.now() after
 * an upload/remove) busts the browser cache so a replace shows immediately.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { cmPhotoUrl } from '@/lib/staffAdmin';
import { initials } from './workspaceFormat';

export type CmPhotoSize = 'sm' | 'md' | 'lg';

// Tailwind size + text presets for the circular avatar.
const SIZE_CLASSES: Record<CmPhotoSize, string> = {
  sm: 'w-6 h-6 text-[10.5px]',
  md: 'w-10 h-10 text-sm',
  lg: 'w-16 h-16 text-base',
};

export function CmPhoto({
  tenantSlug,
  rosterId,
  name,
  size = 'md',
  hasPhoto,
  version,
  className = '',
}: {
  tenantSlug: string;
  rosterId: string;
  name: string | null | undefined;
  size?: CmPhotoSize;
  /** When false, render the initials directly (skip the <img>/404 round-trip). */
  hasPhoto?: boolean;
  /** Cache-bust token; changing it re-attempts the image (e.g. after a replace). */
  version?: string | number;
  className?: string;
}): ReactNode {
  const [errored, setErrored] = useState(false);
  // A new version/roster means a fresh photo to try — clear a prior load error.
  useEffect(() => setErrored(false), [version, rosterId, tenantSlug]);

  const shared = `inline-flex items-center justify-center rounded-pill flex-shrink-0 ${SIZE_CLASSES[size]} ${className}`;

  if (hasPhoto !== false && !errored) {
    return (
      <img
        src={cmPhotoUrl(tenantSlug, rosterId, version)}
        alt={name ? `Photo of ${name}` : 'Care manager photo'}
        loading="lazy"
        className={`${shared} object-cover bg-sage-100`}
        onError={() => setErrored(true)}
        data-testid="cm-photo-img"
      />
    );
  }
  return (
    <span
      className={`${shared} bg-sage-400 text-white font-semibold`}
      title={name ?? undefined}
      aria-label={name ? `${name} (no photo)` : 'No photo'}
      data-testid="cm-photo-initials"
    >
      {initials(name)}
    </span>
  );
}
